import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  LinearProgress,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AddAPhotoOutlined,
  BadgeOutlined,
  CheckCircleRounded,
  EmailOutlined,
  LinkedIn,
  NotificationsOutlined,
  PaletteOutlined,
  PersonOutlined,
  PhoneOutlined,
  RadioButtonUncheckedRounded,
  VerifiedUserOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GlassButton from '../components/GlassButton';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';
import { setThemeMode, setUser } from '../store/coreSlice';
import { openModal } from '../store/modalSlice';
import SocialLinksEditor from '../components/common/SocialLinksEditor';

const CHECKLIST_REMINDER_DELAY = 7 * 24 * 60 * 60 * 1000;
const CHECKLIST_ACTIONS = ['avatar', 'verifyEmail', 'verifyPhone'];

const PostLoginChecklist = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useSelector((state) => state.core.user);
  const businessData = useSelector((state) => state.core.businessData);
  const unreadCount = useSelector((state) => state.notifications.unreadCount);
  const notificationsInitialized = useSelector(
    (state) => state.notifications.initialized
  );
  const hasPendingAction = CHECKLIST_ACTIONS.includes(
    searchParams.get('action') || sessionStorage.getItem('pendingChecklistAction')
  );
  const [hasRequestedAction, setHasRequestedAction] = useState(hasPendingAction);
  const [notificationWaitExpired, setNotificationWaitExpired] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [fieldValue, setFieldValue] = useState('');
  const [fieldError, setFieldError] = useState('');
  const reminderStorageKey = user?.uid && businessData?.id
    ? `nodePostLoginChecklistRemindAt:${user.uid}:${businessData.id}`
    : null;
  const reminderAt = reminderStorageKey
    ? Number(localStorage.getItem(reminderStorageKey))
    : 0;
  const isReminderPending = Number.isFinite(reminderAt) && reminderAt > Date.now();
  const displayName =
    user?.activeBusiness?.publicDisplay?.name || user?.displayName || '';
  const userModules = user?.activeBusiness?.modules || {};
  // /checklist est monté sous AuthLayout, hors MainLayout: la redirection du
  // rôle portail doit être refaite ici.
  const nextPath =
    user?.activeBusiness?.role === 'BILLING'
      ? '/admin/portal'
      : businessData?.formula && userModules.bos !== false
        ? '/bos'
        : businessData?.modules?.includes('PULSE') &&
            userModules.pulse !== false
          ? '/pulse'
          : businessData?.modules?.includes('AURA') &&
              userModules.aura !== false
            ? '/aura'
            : '/';

  useEffect(() => {
    if (!businessData?.id) {
      const action = searchParams.get('action');
      if (CHECKLIST_ACTIONS.includes(action)) {
        sessionStorage.setItem('pendingChecklistAction', action);
      }
      sessionStorage.setItem('showPostLoginChecklist', 'true');
      navigate('/', { replace: true });
    }
  }, [businessData?.id, navigate, searchParams]);

  const customItems = useMemo(() => {
    if (!Array.isArray(businessData?.checklistItems)) return [];

    return businessData.checklistItems
      .filter(
        (item) =>
          item?.isActive !== false &&
          /^[a-zA-Z0-9_-]{1,80}$/.test(item?.id || '')
      )
      .map((item) => ({
        ...item,
        type: 'custom',
        label:
          item[`name_${i18n.language}`] ||
          item[`label_${i18n.language}`] ||
          item.name ||
          item.label ||
          item.id,
      }));
  }, [businessData?.checklistItems, i18n.language]);

  const publicDisplay = user?.activeBusiness?.publicDisplay || {};
  const title =
    publicDisplay.title || user?.activeBusiness?.title || user?.title || '';
  const phone =
    publicDisplay.phone || user?.activeBusiness?.phone || user?.phone || '';
  const linkedin =
    publicDisplay.linkedin ||
    user?.activeBusiness?.linkedin ||
    user?.linkedin ||
    '';
  const selectedTheme = user?.theme || user?.activeBusiness?.theme || '';
  const avatarUrl = user?.activeBusiness?.avatarUrl || user?.avatarUrl || '';
  const checklistState = user?.activeBusiness?.checklistState || {};
  const completedCustomCount = customItems.filter(
    (item) => checklistState[item.id] === true
  ).length;
  const isEmailConfirmed = user?.isEmailConfirmed === true;
  const isPhoneConfirmed = user?.isPhoneConfirmed === true;
  const hasPhone = Boolean(String(phone || '').trim());

  useEffect(() => {
    if (!businessData?.id || !user?.uid) return undefined;
    const action = searchParams.get('action') || sessionStorage.getItem('pendingChecklistAction');
    if (!CHECKLIST_ACTIONS.includes(action)) return undefined;
    // Keep explicit reminder links accessible after their query is consumed.
    setHasRequestedAction(true);
    // Let the app's existing verification listener mount before dispatching.
    const timer = setTimeout(() => {
      sessionStorage.removeItem('pendingChecklistAction');
      if (searchParams.has('action')) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('action');
        setSearchParams(nextParams, { replace: true });
      }
      if (action === 'avatar' && !avatarUrl) {
        dispatch(openModal({ modalType: 'avatarCreation' }));
      } else if ((action === 'verifyEmail' && !isEmailConfirmed) ||
        (action === 'verifyPhone' && hasPhone && !isPhoneConfirmed)) {
        window.dispatchEvent(new CustomEvent('node:open-verification', {
          detail: { type: action === 'verifyPhone' ? 'phone' : 'email' },
        }));
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [businessData?.id, user?.uid, searchParams, setSearchParams, avatarUrl, isEmailConfirmed, isPhoneConfirmed, hasPhone, dispatch]);

  const completableCount = customItems.length + 6 + (hasPhone ? 1 : 0);
  const completedCount =
    completedCustomCount +
    [
      avatarUrl,
      title,
      phone,
      linkedin,
      selectedTheme,
      isEmailConfirmed,
      hasPhone && isPhoneConfirmed,
    ].filter(
      (value) => String(value || '').trim()
    ).length;
  const progress = completableCount
    ? Math.round((completedCount / completableCount) * 100)
    : 100;

  const handleCustomToggle = async (item) => {
    const nextValue = checklistState[item.id] !== true;
    setSavingId(item.id);
    try {
      await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: 'users/me',
        body: { checklistState: { [item.id]: nextValue } },
      });
      dispatch(
        setUser({
          ...user,
          activeBusiness: {
            ...user?.activeBusiness,
            checklistState: {
              ...checklistState,
              [item.id]: nextValue,
            },
          },
        })
      );
    } finally {
      setSavingId(null);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFieldValue(item.value || '');
    setFieldError('');
  };

  const handleProfileSave = async (item, value = fieldValue) => {
    let normalizedValue = String(value || '').trim();
    if (!normalizedValue) return;

    if (item.field === 'phone') {
      const digits = normalizedValue.replace(/\D/g, '');
      const validCharacters = /^\+?[0-9\s().-]+$/.test(normalizedValue);
      if (!validCharacters || digits.length < 7 || digits.length > 15) {
        setFieldError(
          t('invalidPhoneFormat', {
            defaultValue: 'Entrez un numéro de téléphone valide.',
          })
        );
        return;
      }
    }

    if (item.field === 'linkedin') {
      try {
        const candidate = /^https?:\/\//i.test(normalizedValue)
          ? normalizedValue
          : `https://${normalizedValue}`;
        const parsedUrl = new URL(candidate);
        const hostname = parsedUrl.hostname.toLowerCase();
        if (
          !['http:', 'https:'].includes(parsedUrl.protocol) ||
          (hostname !== 'linkedin.com' && !hostname.endsWith('.linkedin.com'))
        ) {
          throw new Error('Invalid LinkedIn URL');
        }
        normalizedValue = parsedUrl.toString();
      } catch (error) {
        setFieldError(
          t('invalidLinkedinUrl', {
            defaultValue: 'Entrez une URL LinkedIn valide.',
          })
        );
        return;
      }
    }

    setSavingId(item.id);
    setFieldError('');
    try {
      const body =
        item.field === 'theme'
          ? { theme: normalizedValue, checklistState: { theme: true } }
          : { [item.field]: normalizedValue };

      const result = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: 'users/me',
        body,
      });
      if (result?.errorData) return;

      if (item.field === 'theme') {
        dispatch(setThemeMode(normalizedValue));
      }
      dispatch(
        setUser({
          ...user,
          ...(item.field === 'theme' ? { theme: normalizedValue } : {}),
          activeBusiness: {
            ...user?.activeBusiness,
            ...(item.field === 'theme' ? { theme: normalizedValue } : {}),
            publicDisplay:
              item.field === 'theme'
                ? publicDisplay
                : { ...publicDisplay, [item.field]: normalizedValue },
            checklistState:
              item.field === 'theme'
                ? { ...checklistState, theme: true }
                : checklistState,
          },
        })
      );
      setEditingId(null);
    } finally {
      setSavingId(null);
    }
  };

  const handleSocialSave = async (networkKey, value) => {
    setSavingId('linkedin');
    try {
      await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: 'users/me',
        body: { [networkKey]: value },
      });
      dispatch(
        setUser({
          ...user,
          activeBusiness: {
            ...user?.activeBusiness,
            publicDisplay: {
              ...publicDisplay,
              [networkKey]: value,
            },
          },
        })
      );
    } catch (err) {
      console.error('Error saving social link in checklist:', err);
    } finally {
      setSavingId(null);
    }
  };

  const checklistItems = [
    {
      id: 'avatar',
      label: t('completeAvatar', {
        defaultValue: 'Créer votre avatar',
      }),
      description: t('completeAvatarDescription', {
        defaultValue: 'Générez un avatar personnalisé à partir d’une photo.',
      }),
      completed: Boolean(avatarUrl),
      icon: AddAPhotoOutlined,
      onClick: () => dispatch(openModal({ modalType: 'avatarCreation' })),
    },
    {
      id: 'jobTitle',
      label: t('completeJobTitle', {
        defaultValue: 'Compléter votre titre de poste',
      }),
      description: title
        ? title
        : t('completeJobTitleDescription', {
            defaultValue: 'Aidez votre équipe à comprendre votre rôle.',
          }),
      completed: Boolean(title),
      icon: BadgeOutlined,
      field: 'title',
      value: title,
    },
    {
      id: 'phone',
      label: t('completePhone', {
        defaultValue: 'Ajouter votre numéro de téléphone',
      }),
      description: t('completePhoneDescription', {
        defaultValue: 'Permettez à votre équipe de vous joindre facilement.',
      }),
      completed: Boolean(phone),
      icon: PhoneOutlined,
      field: 'phone',
      value: phone,
    },
    {
      id: 'linkedin',
      label: t('completeLinkedin', {
        defaultValue: 'Ajouter votre profil LinkedIn et réseaux',
      }),
      description: t('completeLinkedinDescription', {
        defaultValue: 'Ajoutez le lien vers vos profils professionnels et sociaux.',
      }),
      completed: Boolean(linkedin),
      icon: LinkedIn,
      field: 'linkedin',
      value: linkedin,
    },
    {
      id: 'theme',
      label: t('chooseTheme', {
        defaultValue: 'Choisir votre thème',
      }),
      description: t('chooseThemeDescription', {
        defaultValue: 'Sélectionnez une apparence claire ou sombre.',
      }),
      completed: Boolean(selectedTheme),
      icon: PaletteOutlined,
      field: 'theme',
      value: selectedTheme || 'light',
    },
    {
      id: 'verifyEmail',
      label: t('emailVerification', {
        defaultValue: 'Valider votre adresse courriel',
      }),
      description: t('emailVerificationSubtitle', {
        defaultValue: 'Confirmez que votre adresse courriel vous appartient.',
      }),
      completed: isEmailConfirmed,
      icon: EmailOutlined,
      onClick: () =>
        window.dispatchEvent(
          new CustomEvent('node:open-verification', {
            detail: { type: 'email' },
          })
        ),
    },
    {
      id: 'verifyPhone',
      label: t('phoneVerification', {
        defaultValue: 'Valider votre numéro de téléphone',
      }),
      description: t('phoneVerificationSubtitle', {
        defaultValue: 'Confirmez votre numéro avec un code de vérification.',
      }),
      completed: isPhoneConfirmed,
      available: hasPhone,
      icon: VerifiedUserOutlined,
      onClick: () =>
        window.dispatchEvent(
          new CustomEvent('node:open-verification', {
            detail: { type: 'phone' },
          })
        ),
    },
    ...customItems.map((item) => ({
      ...item,
      description:
        item[`description_${i18n.language}`] || item.description || '',
      completed: checklistState[item.id] === true,
      onClick: () => handleCustomToggle(item),
    })),
  ].filter((item) => item.available !== false && !item.completed);

  const hasPendingProfileItems = checklistItems.some(
    (item) => item.type === 'custom' || item.id !== 'linkedin'
  );
  const shouldSkipChecklist = Boolean(
    businessData?.id && user?.uid && user?.activeBusiness &&
    !hasRequestedAction && !hasPendingAction &&
    (isReminderPending ||
      (notificationsInitialized && unreadCount === 0 && !hasPendingProfileItems))
  );

  useEffect(() => {
    if (shouldSkipChecklist) {
      sessionStorage.removeItem('showPostLoginChecklist');
      navigate(nextPath, { replace: true });
    }
  }, [shouldSkipChecklist, navigate, nextPath]);

  useEffect(() => {
    setNotificationWaitExpired(false);
    if (notificationsInitialized) return undefined;
    // Keep the existing checklist usable if the realtime connection is unavailable.
    const timer = setTimeout(() => setNotificationWaitExpired(true), 3000);
    return () => clearTimeout(timer);
  }, [businessData?.id, user?.uid, notificationsInitialized]);

  const handleRemindInOneWeek = () => {
    if (reminderStorageKey) {
      localStorage.setItem(
        reminderStorageKey,
        String(Date.now() + CHECKLIST_REMINDER_DELAY)
      );
    }
    navigate(nextPath);
  };

  if (shouldSkipChecklist) return null;

  // A zero count before the realtime snapshot does not mean there are no alerts.
  if (!hasRequestedAction && !hasPendingAction && !hasPendingProfileItems &&
      !notificationsInitialized && !notificationWaitExpired) {
    return <CircularProgress aria-label={t('loading', { defaultValue: 'Chargement' })} />;
  }

  return (
    <Box
      sx={{
        width: '100%',
        color: 'text.primary',
      }}
    >
      {avatarUrl ? (
        <Avatar src={avatarUrl} sx={{ width: 56, height: 56, mb: 0.5 }} />
      ) : (
        <Typography
          variant="overline"
          sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 1.2 }}
        >
          {businessData?.name}
        </Typography>
      )}
      <Typography
        variant="h4"
        sx={{
          mt: 0.25,
          fontSize: { xs: 27, md: 31 },
          fontWeight: 700,
          letterSpacing: '-0.04em',
        }}
      >
        {displayName
          ? t('welcomeBackNamed', {
              name: displayName,
              defaultValue: 'Bonjour, {{name}}.',
            })
          : t('welcomeBack', { defaultValue: 'Bonjour.' })}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 0.75, mb: 2.25, lineHeight: 1.55 }}
      >
        {t('postLoginChecklistDescription', {
          defaultValue:
            'Prenez un moment pour mettre votre profil à jour et voir ce qui demande votre attention.',
        })}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ flex: 1, height: 6, borderRadius: 99 }}
        />
        <Typography variant="caption" color="text.secondary">
          {completedCount}/{completableCount}
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 1.25,
          p: 1,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'text.secondary',
          backgroundColor: 'action.hover',
        }}
      >
        <NotificationsOutlined sx={{ fontSize: 18, color: 'primary.main' }} />
        <Box>
          <Typography variant="caption" fontWeight={650} color="text.primary">
            {t('sinceLastLogin', {
              defaultValue: 'Depuis votre dernière connexion',
            })}
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: 'block', fontSize: 10.5, lineHeight: 1.25 }}
          >
            {unreadCount > 0
              ? t('unreadNotificationsCount', {
                  count: unreadCount,
                  defaultValue: `${unreadCount} notification(s) non consultée(s)`,
                })
              : t('noUnreadNotifications', {
                  defaultValue: 'Vous êtes à jour.',
                })}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 0.75,
          maxHeight: { xs: 280, md: 300 },
          overflowY: 'auto',
          pr: 0.5,
        }}
      >
        {checklistItems.map((item) => {
          const ItemIcon = item.icon;
          const handleItemClick = item.id === 'linkedin'
            ? undefined
            : item.field
            ? () => handleEdit(item)
            : item.onClick;
          const isInteractive = Boolean(handleItemClick);
          const isEditing = editingId === item.id;
          return (
            <Box
              key={item.id}
              role={isInteractive ? 'button' : undefined}
              tabIndex={isInteractive ? 0 : undefined}
              aria-pressed={item.type === 'custom' ? item.completed : undefined}
              onClick={handleItemClick}
              onKeyDown={(event) => {
                if (
                  isInteractive &&
                  (event.key === 'Enter' || event.key === ' ')
                ) {
                  event.preventDefault();
                  handleItemClick();
                }
              }}
              sx={{
                width: '100%',
                border: '1px solid',
                borderColor: item.completed
                  ? 'success.main'
                  : item.id === 'avatar'
                    ? 'primary.main'
                    : 'divider',
                borderRadius: 2,
                p: 0.85,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: isEditing ? 'flex-start' : 'center',
                gap: 0.85,
                color: 'text.primary',
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.035)'
                    : 'rgba(255,255,255,0.5)',
                cursor: isInteractive ? 'pointer' : 'default',
                transition: 'border-color 160ms ease',
                '&:hover': isInteractive
                  ? { borderColor: 'primary.main' }
                  : undefined,
                ...(item.id === 'avatar' && {
                  animation: 'avatarChecklistPulse 2.4s ease-in-out infinite',
                  '@keyframes avatarChecklistPulse': {
                    '0%, 100%': {
                      boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0.3)}`,
                    },
                    '50%': {
                      boxShadow: `0 0 0 5px ${alpha(theme.palette.primary.main, 0)}`,
                    },
                  },
                }),
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  bgcolor: item.completed ? 'success.main' : 'action.hover',
                  color: item.completed
                    ? 'success.contrastText'
                    : 'primary.main',
                  '& svg': { fontSize: 16 },
                  ...(item.id === 'avatar' && {
                    perspective: '200px',
                    '@keyframes avatarIconFlip': {
                      '0%, 45%': { opacity: 1, transform: 'rotateY(0deg)' },
                      '50%, 95%': { opacity: 0, transform: 'rotateY(90deg)' },
                      '100%': { opacity: 1, transform: 'rotateY(0deg)' },
                    },
                  }),
                }}
              >
                {item.id === 'avatar' ? (
                  <>
                    <Box
                      sx={{
                        gridArea: '1 / 1',
                        display: 'grid',
                        placeItems: 'center',
                        animation: 'avatarIconFlip 4s ease-in-out infinite',
                      }}
                    >
                      <AddAPhotoOutlined />
                    </Box>
                    <Box
                      sx={{
                        gridArea: '1 / 1',
                        display: 'grid',
                        placeItems: 'center',
                        animation: 'avatarIconFlip 4s ease-in-out infinite',
                        animationDelay: '2s',
                      }}
                    >
                      <PersonOutlined />
                    </Box>
                  </>
                ) : ItemIcon ? (
                  <ItemIcon />
                ) : item.completed ? (
                  <CheckCircleRounded />
                ) : (
                  <RadioButtonUncheckedRounded />
                )}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" fontWeight={650}>
                  {item.label}
                </Typography>
                {item.description && !isEditing && item.id !== 'linkedin' && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      mt: 0.05,
                      fontSize: 10.5,
                      lineHeight: 1.25,
                    }}
                  >
                    {item.description}
                  </Typography>
                )}
                {item.id === 'linkedin' && (
                  <Box
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                    sx={{ mt: 0.6, mb: 0.35 }}
                  >
                    <SocialLinksEditor
                      size="small"
                      values={{
                        linkedin: publicDisplay.linkedin || user?.activeBusiness?.linkedin || user?.linkedin || '',
                        facebook: publicDisplay.facebook || user?.activeBusiness?.facebook || user?.facebook || '',
                        instagram: publicDisplay.instagram || user?.activeBusiness?.instagram || user?.instagram || '',
                        tiktok: publicDisplay.tiktok || user?.activeBusiness?.tiktok || user?.tiktok || '',
                        youtube: publicDisplay.youtube || user?.activeBusiness?.youtube || user?.youtube || '',
                      }}
                      onSave={handleSocialSave}
                    />
                  </Box>
                )}
                {isEditing && item.field === 'theme' && (
                  <Box
                    onClick={(event) => event.stopPropagation()}
                    sx={{ display: 'flex', gap: 0.75, mt: 0.75 }}
                  >
                    {['light', 'dark'].map((mode) => (
                      <Button
                        key={mode}
                        size="small"
                        variant={fieldValue === mode ? 'contained' : 'outlined'}
                        onClick={() => {
                          setFieldValue(mode);
                          handleProfileSave(item, mode);
                        }}
                        sx={{
                          minHeight: 28,
                          py: 0.25,
                          textTransform: 'none',
                          fontSize: 11,
                        }}
                      >
                        {mode === 'light'
                          ? t('light', { defaultValue: 'Clair' })
                          : t('dark', { defaultValue: 'Sombre' })}
                      </Button>
                    ))}
                  </Box>
                )}
                {isEditing && item.field !== 'theme' && item.field !== 'linkedin' && (
                  <Box
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                    sx={{ display: 'flex', gap: 0.75, mt: 0.65 }}
                  >
                    <TextField
                      autoFocus
                      size="small"
                      fullWidth
                      value={fieldValue}
                      type={item.field === 'phone' ? 'tel' : 'text'}
                      error={Boolean(fieldError)}
                      helperText={fieldError}
                      onChange={(event) => {
                        setFieldValue(event.target.value);
                        setFieldError('');
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.stopPropagation();
                          handleProfileSave(item);
                        }
                      }}
                      sx={{
                        '& .MuiInputBase-root': { height: 30, fontSize: 11 },
                        '& .MuiFormHelperText-root': {
                          mx: 0,
                          mt: 0.35,
                          fontSize: 9.5,
                        },
                      }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleProfileSave(item)}
                      disabled={!fieldValue.trim() || savingId === item.id}
                      sx={{ minWidth: 64, minHeight: 30, fontSize: 10 }}
                    >
                      {t('save', { defaultValue: 'Enregistrer' })}
                    </Button>
                  </Box>
                )}
              </Box>
              {savingId === item.id ? (
                <CircularProgress size={17} />
              ) : item.type === 'custom' ? (
                <Checkbox checked={item.completed} tabIndex={-1} size="small" />
              ) : item.completed ? (
                <CheckCircleRounded color="success" sx={{ fontSize: 18 }} />
              ) : null}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          mt: 2.5,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        <GlassButton
          variant="text"
          onClick={handleRemindInOneWeek}
          sx={{ width: 'auto', flexShrink: 0 }}
        >
          {t('checklistRemindInOneWeek', { defaultValue: 'Rappeler dans une semaine' })}
        </GlassButton>
        <GlassButton variant="contained" onClick={() => navigate(nextPath)}>
          {t('continue', { defaultValue: 'Continuer' })}
        </GlassButton>
      </Box>
    </Box>
  );
};

export default PostLoginChecklist;
